// Pure conversions from the answers of the new search (GET /search/v2, the
// saved searches) to what the components take: the cards with the words
// found marked, «Abbiamo cercato…», the counts, the messages of the errors.
// No fetching, no React: tested on their own (tests/search-adapt.test.ts).
import { isApiError } from "../../api/client";
import type { components } from "../../api/schema";
import type { NewsItem, TextRun } from "../../data/mock";
import { formatLongDate } from "../../lib/dates";
import { DOCUMENT_TYPE_LABELS } from "../parliament/adapt";
import { toNewsItem, type SourceTree } from "../news/adapt";
import { FINDER_PARAM, finderParam, type FinderTerm } from "../../lib/finder";
import { RESOURCES, foldWord, namedGroups, withChoicesOfWords } from "./params";
import type {
  Chamber,
  PagePeriod,
  Resource,
  SearchMode,
  SearchOrigin,
  SearchParams,
  SearchPeriod,
  SearchSort,
  SearchWords,
  SessionType,
} from "./params";

export type SearchPage = components["schemas"]["SearchV2Page"];
export type SearchHit = components["schemas"]["SearchV2Hit"];
export type Highlight = components["schemas"]["Highlight"];
export type Passage = components["schemas"]["Passage"];
export type Searched = components["schemas"]["Searched"];
export type SearchedTerm = components["schemas"]["SearchedTerm"];
export type OriginCount = components["schemas"]["OriginCount"];
export type SavedSearch = components["schemas"]["SavedSearch"];
export type SavedSearches = components["schemas"]["SavedSearches"];

// ---- the words of the page ----------------------------------------------------

/** The labels of the page's choices, as the tags under the field show them (the design «Pagina Ricerca»). */
export const MODE_LABELS: Record<SearchMode, string> = {
  synonyms: "No plurali e varianti",
  variants: "Plurali e varianti",
  names: "Nome proprio",
  exact: "Solo la parola esatta",
};
export const WORDS_LABELS: Record<SearchWords, string> = {
  all: "Tutte le parole",
  any: "Almeno una parola",
  phrase: "Frase esatta",
};
/** «Dove cercare» (decisions 28, 43): News and Parlamento, one of them, or the sources of Il mio feed (the pins). */
export type Where = "all" | "news" | "parliament" | "followed";
export const WHERE_LABELS: Record<Where, string> = {
  all: "News e Parlamento",
  news: "Solo le News",
  parliament: "Solo il Parlamento",
  followed: "Solo «Il mio feed»",
};
/** What each choice of a menu means, under its name in the open menu (Davide, 3/11: the names alone were not clear). */
export const WHERE_NOTES: Record<Where, string> = {
  all: "Gli articoli delle News e i documenti di Camera e Senato.",
  news: "Solo gli articoli delle fonti di News.",
  parliament: "Solo i documenti di Camera e Senato: resoconti, allegati, dossier.",
  followed: "Solo le fonti con il pin, quelle dei tuoi Preferiti in «Il mio feed».",
};
export const WORDS_NOTES: Record<SearchWords, string> = {
  all: "Ogni parola deve esserci, anche lontana dalle altre.",
  any: "Basta una delle parole: trovi più documenti.",
  phrase: "Le parole una accanto all’altra, nell’ordine in cui le scrivi.",
};
export function whereOf(p: { origin: SearchOrigin | null; followedOnly: boolean }): Where {
  if (p.followedOnly) return "followed";
  return p.origin === "orchestra" ? "news" : p.origin === "coro" ? "parliament" : "all";
}
export const SORT_LABELS: Record<SearchSort, string> = {
  recent: "Ordina più recenti",
  oldest: "Ordina meno recenti",
  relevance: "Ordina più rilevanti",
};
/** The same choice in the menu above the results. */
export const RESULTS_SORT_LABELS: Record<SearchSort, string> = {
  recent: "Risultati più recenti",
  oldest: "Risultati meno recenti",
  relevance: "Risultati più rilevanti",
};
export const PERIOD_LABELS: Record<SearchPeriod, string> = {
  "7d": "Ultimi 7 giorni",
  "1m": "Ultimo mese",
  "6m": "Ultimi 6 mesi",
  all: "Tutto l’archivio",
};

/** 2026-05-01 → 01/05/26, as the design writes the days of «Da… a…». */
export function shortDay(day: string): string {
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${(y ?? "").slice(2)}`;
}

/** «Ultimi 6 mesi», or «Da 01/05/26 a 01/06/26». */
export function periodLabel(period: PagePeriod, dateFrom: string | null, dateTo: string | null): string {
  if (period === "range") return dateFrom && dateTo ? `Da ${shortDay(dateFrom)} a ${shortDay(dateTo)}` : "Da… a…";
  return PERIOD_LABELS[period];
}
export const ORIGIN_LABELS: Record<SearchOrigin | "all", string> = {
  all: "Tutto",
  orchestra: "News",
  coro: "Parlamento",
};
export const CHAMBER_LABELS: Record<Chamber, string> = { camera: "Camera", senato: "Senato" };
export const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  plenaria: "Plenaria",
  commissioni: "Commissioni",
};

/**
 * The document types of the advanced filter: the 16 values of the contract's
 * closed list (`resource`), one choice per label, the types with the same
 * label together («Ordine del giorno»: odg_giorno and ordine_del_giorno). The
 * labels are the ones the cards already use (DOCUMENT_TYPE_LABELS).
 */
export const RESOURCE_CHOICES: { label: string; values: Resource[] }[] = (() => {
  const byLabel = new Map<string, Resource[]>();
  for (const value of RESOURCES) {
    const label = DOCUMENT_TYPE_LABELS[value] ?? value;
    byLabel.set(label, [...(byLabel.get(label) ?? []), value]);
  }
  return [...byLabel].map(([label, values]) => ({ label, values }));
})();

// ---- the words found, marked ----------------------------------------------------

const isHigh = (unit: number) => unit >= 0xd800 && unit <= 0xdbff;
const isLow = (unit: number) => unit >= 0xdc00 && unit <= 0xdfff;

/**
 * The text as runs, the highlights `strong` (the look of a marked word on the
 * cards, TextRun). Positions are UTF-16 code units, as String.prototype.slice
 * takes them: an accented letter is one unit (two when written as a letter
 * and a combining accent), an emoji two. Defensive, since a wrong position
 * must never break the card: a highlight outside the text is cut to it, an
 * empty or overlapping one is dropped, and a boundary that falls between the
 * two halves of an emoji moves out so the emoji stays whole.
 */
export function highlightRuns(text: string, highlights: readonly Highlight[]): TextRun[] {
  const marks = [...highlights]
    .map(({ start, end }) => {
      let s = Math.max(0, Math.min(Math.trunc(start), text.length));
      let e = Math.max(0, Math.min(Math.trunc(end), text.length));
      if (s > 0 && s < text.length && isLow(text.charCodeAt(s)) && isHigh(text.charCodeAt(s - 1))) s -= 1;
      if (e > 0 && e < text.length && isLow(text.charCodeAt(e)) && isHigh(text.charCodeAt(e - 1))) e += 1;
      return { start: s, end: e };
    })
    .filter(({ start, end }) => end > start)
    .sort((a, b) => a.start - b.start);

  const runs: TextRun[] = [];
  let at = 0;
  for (const mark of marks) {
    if (mark.start < at) continue; // overlaps the one before
    if (mark.start > at) runs.push({ text: text.slice(at, mark.start) });
    runs.push({ text: text.slice(mark.start, mark.end), strong: true });
    at = mark.end;
  }
  if (at < text.length) runs.push({ text: text.slice(at) });
  return runs;
}

const ELLIPSIS = "…";

/**
 * The passage of a hit as runs, with «…» where the text goes on (cut_before,
 * cut_after). The contract's examples already carry the «…» inside `text`:
 * it is not added a second time.
 */
export function passageRuns(passage: Passage): TextRun[] {
  const runs = highlightRuns(passage.text, passage.highlights);
  if (runs.length === 0) return runs;
  if (passage.cut_before && !passage.text.startsWith(ELLIPSIS)) runs.unshift({ text: ELLIPSIS });
  if (passage.cut_after && !passage.text.endsWith(ELLIPSIS)) runs.push({ text: ELLIPSIS });
  return runs;
}

/**
 * A hit as the card of the lists (toNewsItem: chips, date, chevron, bookmark),
 * with the passage around the words found in place of the start of the text.
 * Without a passage (a match in the title only, a document without text) the
 * card keeps the start of the text. A search of several words shows the
 * first place of each word (`passages`), one after the other. The title stays plain: the card has no
 * way to mark words in it (see docs/grafica/segnalazioni.md).
 */
export function toSearchNewsItem(
  hit: SearchHit,
  tree: Pick<SourceTree, "active"> | null = null,
  finder: FinderTerm[] = [],
): NewsItem {
  const found = toNewsItem(hit, tree);
  // the words go with the link, so the document opens on them (decision 27)
  const item =
    finder.length > 0
      ? {
          ...found,
          pageUrl: `${found.pageUrl}${found.pageUrl.includes("?") ? "&" : "?"}${FINDER_PARAM}=${encodeURIComponent(finderParam(finder))}`,
        }
      : found;
  // the open preview (3/11): the same places with more text around, a blank line between them;
  // none (a match in the title only, no text): the start of the text, as in the News
  const previews = hit.previews ?? [];
  const body = previews.flatMap((p, i) => [...(i > 0 ? [[]] : []), passageRuns(p)]);
  // several words (decision 27): the first place of each, one after the other, «…» between them
  const passages = hit.passages ?? [];
  if (passages.length > 0) {
    const runs = passages.flatMap((p, i) => [...(i > 0 ? [{ text: " " }] : []), ...passageRuns(p)]);
    return { ...item, excerpt: runs, body };
  }
  return hit.passage ? { ...item, excerpt: passageRuns(hit.passage), body } : { ...item, body };
}

/**
 * The words of «Parole cercate» in the document: each word searched (not the excluded ones), with every form
 * found and what the lexicon added. A word searched as a proper name (`names`: names_for, or every word in
 * mode «names») is marked `cased`, so the document marks «Fini» and not «ai fini».
 */
export function finderTermsOf(
  searched: Searched,
  names: { all: boolean; words: string[] } = { all: false, words: [] },
): FinderTerm[] {
  const named = new Set(names.words.map((w) => w.toLowerCase()));
  return searched.all_of.flatMap((group) =>
    group.any_of.map((term) => ({
      ...(term.kind === "word" && (names.all || named.has(term.written.replace(/^"|"$/g, "").toLowerCase()))
        ? { cased: true }
        : {}),
      label: term.kind === "prefix" ? term.written : term.written.replace(/^"|"$/g, ""),
      forms: [
        ...(term.kind === "prefix" ? [term.written.replace(/\*$/, "")] : term.forms),
        ...term.added.map((a) => a.text),
      ],
      prefix: term.kind === "prefix",
    })),
  );
}

// ---- «Abbiamo cercato…» ----------------------------------------------------------

const quoted = (text: string) => `«${text}»`;

/** One term as the sentence names it: its forms, a phrase in quotes, a prefix, and what was added to it. */
function termLabel(term: SearchedTerm): string {
  const written = term.written.replace(/^-/, "");
  let label: string;
  if (term.kind === "prefix") label = `le parole che iniziano con ${quoted(written.replace(/\*$/, ""))}`;
  else if (term.kind === "phrase") label = (term.forms.length > 0 ? term.forms : [written]).map(quoted).join(", ");
  // one form: as the user wrote it («PNRR», not «pnrr»); more: every form, the written one first
  else label = term.forms.length > 1 ? term.forms.join(", ") : written;
  const added = term.added.map((a) => quoted(a.text));
  return added.length > 0 ? `${label} (anche ${added.join(", ")})` : label;
}

/**
 * The line under «Hai cercato:» from `searched`: the groups that must all be
 * there (each a choice of terms joined by «oppure», in brackets when there
 * are more groups), the words excluded and the words left out. The choices
 * (plurali, parole…) are in the tags above, not here.
 */
export function searchedSentence(searched: Searched): string {
  const groups = searched.all_of
    .map((group) => group.any_of.map(termLabel).join(" oppure "))
    .filter((g) => g.length > 0);
  const many = groups.length > 1;
  const parts = [groups.map((g) => (many && g.includes(" oppure ") ? `(${g})` : g)).join(" e ")];
  if (searched.none_of.length > 0) parts.push(`escludendo ${searched.none_of.map(termLabel).join(", ")}`);
  if (searched.ignored.length > 0)
    parts.push(`senza le parole troppo comuni ${searched.ignored.map(quoted).join(", ")}`);
  return parts.join(", ");
}

/** «Varianti: legge (singolare e plurale), bila*»: what each word takes, in the order given. */
export function variantsLabel(plurals: readonly string[], beginnings: readonly string[] = []): string {
  return `Varianti: ${[...plurals.map((w) => `${w} (singolare e plurale)`), ...beginnings.map((b) => `${b}*`)].join(", ")}`;
}

/** «Nome proprio: matteo, salvini»: the words of «Impostazioni sulle singole parole» searched as a name. */
export function namesLabel(names: readonly string[]): string {
  return `Nome proprio: ${names.join(", ")}`;
}

/** «Persona: Matteo Salvini (anche SALVINI Matteo)»: named words next to each other, one person. */
export function personLabel(words: readonly string[], surnameAlone = false): string {
  const cap = (w: string) => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}`;
  const name = words.map(cap).join(" ");
  if (words.length > 3) return `Persona: ${name}`;
  const [first, ...rest] = words;
  const other = [...rest.map((w) => w.toUpperCase()), cap(first!)].join(" ");
  // Loggione's rules v6 (5/11): also the surname alone, the last word typed; not in «Frase esatta»
  const surname = surnameAlone ? `, o solo ${cap(words[words.length - 1]!)}` : "";
  return `Persona: ${name} (anche ${other}${surname})`;
}

/** A choice shown as a tag: its words and, when it is not the default, what its × puts back. */
export type ChoiceTag = { key: string; label: string; reset?: Partial<SearchParams> };

/**
 * The tags under the field: the sort, where, the period and the words always (as the
 * design shows them; no plurals, the default, is not said), then the choices that are on. The × of a tag puts its choice back
 * to the default; the default ones have none.
 */
export function choiceTags(params: SearchParams): ChoiceTag[] {
  const p = withChoicesOfWords(params);
  const tags: ChoiceTag[] = (
    [
      { key: "sort", label: SORT_LABELS[p.sort], ...(p.sort !== "recent" ? { reset: { sort: "recent" } } : {}) },
      {
        key: "where",
        label: WHERE_LABELS[whereOf(p)],
        ...(whereOf(p) !== "all" ? { reset: { origin: null, followedOnly: false } } : {}),
      },
      {
        key: "period",
        label: periodLabel(p.period, p.dateFrom, p.dateTo),
        ...(p.period !== "6m" ? { reset: { period: "6m", dateFrom: null, dateTo: null } } : {}),
      },
      { key: "words", label: WORDS_LABELS[p.words], ...(p.words !== "all" ? { reset: { words: "all" } } : {}) },
      p.mode === "synonyms" && (p.variantsFor.length > 0 || Object.keys(p.prefixFor).length > 0)
        ? {
            key: "mode",
            label: variantsLabel(p.variantsFor, Object.values(p.prefixFor)),
            reset: { variantsFor: [], prefixFor: {} },
          }
        : // no plurals is the default: said nowhere, it only confused (Davide, 3/11)
          p.mode !== "synonyms"
          ? { key: "mode", label: MODE_LABELS[p.mode], reset: { mode: "synonyms" } }
          : null,
    ] as (ChoiceTag | null)[]
  ).filter((tag): tag is ChoiceTag => tag !== null);
  // a person (2 or 3 names next to each other) is said as one, in any order; the other names together
  const groups = namedGroups(p.q, p.namesFor);
  for (const person of groups.filter((g) => g.length > 1)) {
    const keys = person.map(foldWord);
    tags.push({
      key: `person-${keys.join("-")}`,
      label: personLabel(person, p.words !== "phrase"),
      reset: { namesFor: p.namesFor.filter((k) => !keys.includes(k)) },
    });
  }
  const single = groups.filter((g) => g.length === 1).map((g) => foldWord(g[0]!));
  if (single.length > 0)
    tags.push({
      key: "names",
      label: namesLabel(single),
      reset: { namesFor: p.namesFor.filter((k) => !single.includes(k)) },
    });
  for (const chamber of p.chamber ?? [])
    tags.push({
      key: `chamber-${chamber}`,
      label: CHAMBER_LABELS[chamber],
      reset: { chamber: (p.chamber ?? []).filter((c) => c !== chamber) },
    });
  for (const type of p.sessionType ?? [])
    tags.push({
      key: `session-${type}`,
      label: SESSION_TYPE_LABELS[type],
      reset: { sessionType: (p.sessionType ?? []).filter((t) => t !== type) },
    });
  for (const choice of RESOURCE_CHOICES.filter((c) => c.values.every((v) => p.resource?.includes(v))))
    tags.push({
      key: `resource-${choice.label}`,
      label: choice.label,
      reset: { resource: (p.resource ?? []).filter((r) => !choice.values.includes(r)) },
    });
  return tags;
}

// ---- counts and labels ------------------------------------------------------------

// "always": Italian leaves a number of four digits without the dot (1000), the page writes «1.000»
const number = new Intl.NumberFormat("it-IT", { useGrouping: "always" });

/** «214», «oltre 1.000» when the api stopped counting. */
export function countLabel(count: OriginCount): string {
  return count.capped ? `oltre ${number.format(count.count)}` : number.format(count.count);
}

/** «Tutto · oltre 1.000», «News · 214»: a choice of the origin selector with its count. */
export function originChoiceLabel(origin: SearchOrigin | "all", counts: SearchPage["counts"] | null): string {
  return counts ? `${ORIGIN_LABELS[origin]} · ${countLabel(counts[origin])}` : ORIGIN_LABELS[origin];
}

/** «1 risultato», «214 risultati», «oltre 1.000 risultati». */
export function resultsLabel(count: OriginCount): string {
  return !count.capped && count.count === 1 ? "1 risultato" : `${countLabel(count)} risultati`;
}

/** The days searched, beside the count: «dal 2 maggio 2026 al 2 novembre 2026», or «fino al …» for the whole archive. */
export function rangeLabel(page: Pick<SearchPage, "date_from" | "date_to">): string {
  const to = formatLongDate(page.date_to);
  return page.date_from ? `dal ${formatLongDate(page.date_from)} al ${to}` : `fino al ${to}`;
}

/** The count of the origin the page shows. */
export function countOf(page: Pick<SearchPage, "counts">, origin: SearchOrigin | null): OriginCount {
  return page.counts[origin ?? "all"];
}

/** «4425 documenti trovati», «1 documento trovato», «oltre 1.000 documenti trovati». */
export function foundLabel(count: OriginCount): string {
  return !count.capped && count.count === 1 ? "1 documento trovato" : `${countLabel(count)} documenti trovati`;
}

/** «Ci sono anche risultati più vecchi di 6 mesi»: the period as the note names it. */
export function olderNote(period: SearchPeriod): string {
  const span: Record<SearchPeriod, string> = { "7d": "7 giorni", "1m": "un mese", "6m": "6 mesi", all: "" };
  return `Ci sono anche risultati più vecchi di ${span[period]}.`;
}

// ---- the errors, in words a consultant can act on ---------------------------------

export const SEARCH_PROBLEM = "urn:politorai:problem:search:";

/** How the page treats a failed search: the switch is off (the page of today), or a message with or without «Riprova». */
export type SearchFailure = { kind: "off" } | { kind: "message"; message: string; retry: boolean };

export const NO_SEARCHABLE_WORDS =
  "Non c’è una parola da cercare: le parole scritte sono troppo comuni (articoli, preposizioni) o sono solo simboli. Scrivi almeno una parola con un significato, per esempio «decreto».";
export const prefixTooShort = (letters: number) =>
  `Con l’asterisco servono almeno ${letters} lettere prima di *: per esempio «sanitar*», non «pro*». Allunga l’inizio della parola o togli l’asterisco.`;
export const QUERY_TOO_COMPLEX =
  "La ricerca ha troppe parole o troppe varianti. Togli qualche parola, oppure spegni «Anche plurali e varianti» in «Impostazioni avanzate».";
export const TOO_BROAD =
  "La ricerca trova troppi documenti ed è stata interrotta. Aggiungi una parola o scegli un periodo più breve.";
export const INVALID_SEARCH = "La ricerca non è valida. Controlla le parole e i filtri, poi riprova.";
export const SEARCH_FAILED = "Ricerca non riuscita.";

/** The message of a 422 of the words (GET /search/v2, POST /saved-searches); null when it is not one. */
function wordsMessage(error: unknown): string | null {
  if (!isApiError(error) || error.status !== 422) return null;
  const type = error.problem?.type ?? "";
  const limit = typeof error.problem?.limit === "number" ? error.problem.limit : null;
  switch (type) {
    case `${SEARCH_PROBLEM}no-searchable-words`:
      return NO_SEARCHABLE_WORDS;
    case `${SEARCH_PROBLEM}prefix-too-short`:
      return prefixTooShort(limit ?? 4);
    case `${SEARCH_PROBLEM}query-too-complex`:
      return QUERY_TOO_COMPLEX;
    case `${SEARCH_PROBLEM}too-broad`:
      return TOO_BROAD;
    default:
      return null;
  }
}

/**
 * A failed GET /search/v2. 404 is the switch turned off for the caller (or a
 * feed that is not theirs, which the page never sends on its own): it gives back
 * the page of today. A 422 of the words says what to change; one of the contract
 * says the search is not valid; nothing of those can be retried as it is.
 * Anything else may pass: «Riprova».
 */
export function searchFailure(error: unknown): SearchFailure {
  if (isApiError(error) && error.status === 404) return { kind: "off" };
  const words = wordsMessage(error);
  if (words) return { kind: "message", message: words, retry: false };
  if (isApiError(error) && error.status === 422) return { kind: "message", message: INVALID_SEARCH, retry: false };
  return { kind: "message", message: SEARCH_FAILED, retry: true };
}

// ---- Le mie ricerche -------------------------------------------------------------------

export const NAME_TAKEN = "Hai già una ricerca con questo nome: scegline un altro.";
export const savedLimit = (max: number) =>
  `Hai già ${max} ricerche salvate, il massimo: cancellane una per salvarne un’altra.`;
export const SAVED_GONE = "Questa ricerca salvata non c’è più.";
export type SavedAction = "create" | "rename" | "move" | "delete";
const SAVED_FAILED: Record<SavedAction, string> = {
  create: "Salvataggio della ricerca non riuscito.",
  rename: "Cambio del nome non riuscito.",
  move: "Spostamento nella cartella non riuscito.",
  delete: "Eliminazione non riuscita.",
};

/** A refused call of Le mie ricerche, in words, and whether sending it again may help. */
export function savedSearchError(error: unknown, action: SavedAction): { message: string; retry: boolean } {
  if (isApiError(error)) {
    if (error.status === 409) return { message: NAME_TAKEN, retry: false };
    if (error.status === 422 && error.problem?.type === `${SEARCH_PROBLEM}saved-limit`) {
      const limit = typeof error.problem.limit === "number" ? error.problem.limit : 50;
      return { message: savedLimit(limit), retry: false };
    }
    const words = wordsMessage(error);
    if (words) return { message: words, retry: false };
    if (error.status === 422) return { message: INVALID_SEARCH, retry: false };
    if (error.status === 404) return { message: SAVED_GONE, retry: false };
  }
  return { message: SAVED_FAILED[action], retry: true };
}

/** Whether a name is another saved search's already (case and blanks aside, as the api compares them). */
export function nameTaken(searches: readonly SavedSearch[], name: string, exceptId: string | null = null): boolean {
  const wanted = name.trim().toLowerCase();
  return searches.some((s) => s.id !== exceptId && s.name.trim().toLowerCase() === wanted);
}

/**
 * What the page says when a saved search was reopened without its feed: the
 * feed (Il mio feed) was deleted since, the api left its `feed_id` out
 * (`feed_gone`), the search looked at every source.
 */
export const FEED_GONE = "Il feed che usavi non esiste più: abbiamo cercato in tutte le fonti.";

/** The location state of the link that reopens a saved search: whether its feed is gone. */
export type ReopenState = { savedFeedGone: true };

/** The state of a link that reopens the saved search, or none. */
export function reopenState(search: Pick<SavedSearch, "feed_gone">): ReopenState | undefined {
  return search.feed_gone ? { savedFeedGone: true } : undefined;
}

/**
 * The state of the link of a saved search (decision 44): the search in the form, not sent, so it
 * can be changed before «Cerca»; the parameters as the URL writes them.
 */
export type SavedDraftState = { savedDraft: string; savedFeedGone?: true };
export function savedDraftState(search: Pick<SavedSearch, "feed_gone">, draft: URLSearchParams): SavedDraftState {
  return { savedDraft: draft.toString(), ...reopenState(search) };
}

/** The search of a saved search to put in the form (the location state of its link), or null. */
export function savedDraftOf(state: unknown): URLSearchParams | null {
  return typeof state === "object" && state !== null && "savedDraft" in state && typeof state.savedDraft === "string"
    ? new URLSearchParams(state.savedDraft)
    : null;
}

/** Whether the page was opened from a saved search whose feed is gone (the location state of its link). */
export function feedGoneOf(state: unknown): boolean {
  return typeof state === "object" && state !== null && "savedFeedGone" in state && state.savedFeedGone === true;
}

/** The tags under a saved search in «Ricerche salvate»: its choices, as the tags of the page say them. */
export function savedSearchTags(search: SavedSearch): string[] {
  const q = search.query;
  const beginnings = (q.prefix_for ?? []).map((pair) => pair.split(":")[1] ?? pair);
  const variants =
    q.mode === "synonyms" && (q.variants_for?.length || beginnings.length)
      ? variantsLabel(q.variants_for ?? [], beginnings)
      : null;
  const mode = variants ?? (q.mode !== "synonyms" ? MODE_LABELS[q.mode] : null);
  const where = WHERE_LABELS[whereOf({ origin: q.origin ?? null, followedOnly: q.followed_only ?? false })];
  const tags = [SORT_LABELS[q.sort], where, PERIOD_LABELS[q.period], WORDS_LABELS[q.words], ...(mode ? [mode] : [])];
  if (q.names_for?.length) tags.push(namesLabel(q.names_for));
  if (q.chamber?.length || q.session_type?.length || q.resource?.length) tags.push("Filtri del Parlamento");
  if (search.feed_gone) tags.push("Il feed non esiste più");
  return tags;
}

/** The saved searches in their folders (A→Z), then the ones without a folder; each list as the api gives it (newest first). */
export function savedSearchFolders(items: readonly SavedSearch[]): {
  folders: { name: string; items: SavedSearch[] }[];
  loose: SavedSearch[];
} {
  const byFolder = new Map<string, SavedSearch[]>();
  const loose: SavedSearch[] = [];
  for (const item of items) {
    if (item.folder) byFolder.set(item.folder, [...(byFolder.get(item.folder) ?? []), item]);
    else loose.push(item);
  }
  const folders = [...byFolder]
    .sort(([a], [b]) => a.localeCompare(b, "it"))
    .map(([name, list]) => ({ name, items: list }));
  return { folders, loose };
}

// ---- the bookmarks in the cached pages ---------------------------------------------------

/** A page of hits with one item's bookmark changed. */
export function applySavedToSearch(
  page: SearchPage,
  change: { origin: SearchHit["origin"]; id: string; saved: boolean },
): SearchPage {
  if (!page.items.some((h) => h.origin === change.origin && h.id === change.id)) return page;
  return {
    ...page,
    items: page.items.map((h) =>
      h.origin === change.origin && h.id === change.id ? { ...h, saved: change.saved } : h,
    ),
  };
}

/** A page of hits without any bookmark («Pulisci tutto» in News salvate). */
export function withoutSearchBookmarks(page: SearchPage): SearchPage {
  return page.items.some((h) => h.saved) ? { ...page, items: page.items.map((h) => ({ ...h, saved: false })) } : page;
}
